import { isEmpty } from '@ember/utils'
import Component from '@glimmer/component'
import { action } from '@ember/object'
import { intervalToDuration, formatDuration } from 'date-fns'
import { tracked } from '@glimmer/tracking'
import { on } from '@ember/modifier'
import { t, formatDate } from 'ember-intl'
import { SectionBoxHeader } from 'teacher-ui-client/components/assignments-admin/section-box-header'
import { gt, not, and } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type Student from 'teacher-ui-client/models/student'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import { posthogEvent } from 'teacher-ui-client/modifiers/posthog'
import type { AssignmentPrecinct } from 'teacher-ui-client/models/assignment'

interface Signature {
  Element: HTMLDivElement
  Args: {
    assignmentProduct: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
    assignmentPrecinct?: AssignmentPrecinct
    students?: Student[]
    startAt?: Date
    endAt?: Date
    activityText?: string
    selectedAssignmentOption?: AssignmentOption
    submitAction: () => void | Promise<void>
  }
}

export class AssignmentDetails extends Component<Signature> {
  @tracked showAllStudents = false

  @tracked submitting = false

  get disableFormSubmit() {
    const hasInvalidProperties = this.missingRequiredProperties.length > 0

    return hasInvalidProperties || this.submitting || this.missingRequiredChapters
  }

  get durationFormatted() {
    if (this.args.startAt && this.args.endAt) {
      const duration = intervalToDuration({ start: this.args.startAt, end: this.args.endAt })
      return formatDuration(duration, { format: ['years', 'months', 'weeks', 'days'], delimiter: ', ' })
    }
    return null
  }

  get headingClass() {
    switch (this.args.assignmentProduct) {
      case 'maths':
        return 'text-ms-green-300'
      case 're':
      case 'rex':
      case 'fast_phonics':
        return 'text-oceany-blue-300'
      case 'writing_legends':
        return 'text-wl-blue-300'
      default:
        return ''
    }
  }

  /**
   * Invalid if selected book has chapters with quizzes, yet none were selected
   */
  get missingRequiredChapters() {
    return (
      this.args.selectedAssignmentOption?.book?.chapters.some((chapter) => chapter.has_quiz) &&
      !this.args.selectedAssignmentOption.assignmentData.chapters?.length
    )
  }

  get missingRequiredProperties() {
    return [this.args.students, this.args.selectedAssignmentOption, this.args.startAt, this.args.endAt].filter(isEmpty)
  }

  get studentNames() {
    const names = this.args.students?.map((student) => `${student.firstName} ${student.lastName.charAt(0)}.`) ?? []
    return this.showAllStudents ? names.join(', ') : names.slice(0, 10).join(', ')
  }

  get selectedChapters() {
    const { selectedAssignmentOption } = this.args
    return (
      selectedAssignmentOption?.book?.chapters.filter((_chapter, index) => {
        const chapterId = index + 1
        return selectedAssignmentOption?.assignmentData.chapters?.includes(chapterId)
      }) ?? []
    )
  }

  get isReadAloudAssignment() {
    const assignmentType = this.args.selectedAssignmentOption?.assignmentData.assignment_type

    if (assignmentType === 'read_aloud_book' || assignmentType === 'read_aloud_chapter') {
      return true
    }
    return false
  }

  @action
  async submitAssignment() {
    try {
      this.submitting = true
      await this.args.submitAction()
    } finally {
      // we want to release the button no matter if success or fail
      this.submitting = false
    }
  }

  @action
  showAllStudentsAction() {
    this.showAllStudents = !this.showAllStudents
  }

  <template>
    <div class="rounded-lg border-none shadow-md" ...attributes>
      <SectionBoxHeader
        @label={{t "assignments.assignmentDetails.title"}}
        @icon="phosphor-paperclip-bold"
        @appearance={{@assignmentProduct}}
      />
      <div class="max-h-[calc(100vh-12rem)] overflow-y-auto p-8">
        <div data-test-assignment-details-students>
          <h4 class={{this.headingClass}}>
            {{t "assignments.assignmentDetails.assignedTo"}}
          </h4>
          {{#if this.studentNames}}
            <p class="mb-3">
              {{this.studentNames}}
            </p>
            {{#if (gt @students.length 10)}}
              <div
                class="mb-2 cursor-pointer"
                data-test-assignment-details-toggle
                role="button"
                {{on "click" this.showAllStudentsAction}}
              >
                {{#if this.showAllStudents}}
                  <PhosphorIcon @icon="phosphor-caret-circle-up-fill" />
                {{else}}
                  <PhosphorIcon @icon="phosphor-caret-circle-down-fill" />
                {{/if}}
                {{if
                  this.showAllStudents
                  (t "assignments.assignmentDetails.hide")
                  (t "assignments.assignmentDetails.show")
                }}
              </div>
            {{/if}}
          {{else}}
            <p class="text-dusty-black-200 mb-3 italic">
              {{t "assignments.assignmentDetails.noStudents"}}
            </p>
          {{/if}}
        </div>
        <div data-test-assignment-details-start>

          <h4 class={{this.headingClass}}>
            {{t "assignments.assignmentDetails.startDate"}}
          </h4>
          {{#if @startAt}}
            <p class="mb-3">
              {{formatDate @startAt weekday="short" day="2-digit" month="short" year="2-digit"}}
            </p>
          {{else}}
            <p class="text-dusty-black-200 mb-3 italic">
              {{t "assignments.assignmentDetails.noStartDateSelected"}}
            </p>
          {{/if}}
        </div>
        <div data-test-assignment-details-due>
          <h4 class={{this.headingClass}}>
            {{t "assignments.assignmentDetails.dueDate"}}
          </h4>
          {{#if @endAt}}
            <p class="mb-3">
              {{formatDate @endAt weekday="short" day="2-digit" month="short" year="2-digit"}}
            </p>
          {{else}}
            <p class="text-dusty-black-200 mb-3 italic">
              {{t "assignments.assignmentDetails.noDueDateSelected"}}
            </p>
          {{/if}}
        </div>
        <div data-test-assignment-details-duration>
          <h4 class={{this.headingClass}}>
            {{t "assignments.assignmentDetails.duration"}}
          </h4>
          {{#if this.durationFormatted}}
            <p class="mb-3">
              {{this.durationFormatted}}
            </p>
          {{else}}
            <p class="text-dusty-black-200 mb-3 italic">
              {{t "assignments.assignmentDetails.durationNone"}}
            </p>
          {{/if}}
        </div>
        <div data-test-assignment-details-activity>
          <h4 class={{this.headingClass}}>
            {{t "assignments.assignmentDetails.activity"}}
          </h4>
          {{#if @activityText}}
            <p class="mb-3">
              {{@activityText}}
            </p>
          {{else}}
            <p class="text-dusty-black-200 mb-3 italic">
              {{t "assignments.assignmentDetails.noActivityAdded"}}
            </p>
          {{/if}}
        </div>
        <div data-test-assignment-details-activity-parts>
          <ul class="list-unstyled">
            {{#each this.selectedChapters as |chapter|}}
              <li
                data-after=", "
                class="inline-block after:relative after:content-[attr(data-after)] last:after:hidden"
              >
                {{chapter.name}}
              </li>
            {{else}}
              {{#if this.missingRequiredChapters}}
                <li class="text-dusty-black-200 italic">{{t "assignments.assignmentDetails.noChaptersAdded"}}</li>
              {{/if}}
            {{/each}}
          </ul>
        </div>
        {{#if (and this.isReadAloudAssignment (not this.disableFormSubmit))}}
          <div data-test-audio-notice class="mt-8">
            <p class="mb-3">{{t "assignments.assignmentDetails.readAloudNotice"}}</p>
          </div>
        {{/if}}

        <UiButton
          data-test-create-assignment
          class="mt-8"
          disabled={{this.disableFormSubmit}}
          @appearance={{ButtonAppearance.regular}}
          {{posthogEvent
            "assignment-created"
            studentCount=@students.length
            grade=@selectedAssignmentOption.gradePosition
            product=@assignmentProduct
            assignmentPrecinct=@assignmentPrecinct
            lessonData=@selectedAssignmentOption.assignmentData
          }}
          {{on "click" this.submitAssignment}}
        >
          <PhosphorIcon @icon={{if this.disableFormSubmit "phosphor-lock-fill" "phosphor-plus-bold"}} />
          {{t "assignments.assignmentDetails.createAssignmentButtonLabel"}}
        </UiButton>
      </div>
    </div>
  </template>
}
